import { convertAdjustmentValue } from "~/models/adjustment"
import { formatDate } from "~/utils/datetime"

/**
 * One adjustment in a hover list: name with the entered value, then a subtle meta line ending in
 * the date range. Has no surface of its own, so both the grid `Adjustment` tooltip and the
 * adjustment hover cards can render it; the parent owns the row dividers.
 */
export function AdjustmentRow({
  name,
  value,
  type,
  startDate,
  endDate,
  detail,
  valueInDetail = false,
  leading,
  trailing,
  children,
}: {
  name: string
  value: number
  type: string
  startDate: Date
  endDate: Date
  /** Leads the meta line, e.g. the theme or the adjustment type. */
  detail?: string
  /** Shows the value on the meta line instead of the title, freeing the title for `trailing`. */
  valueInDetail?: boolean
  leading?: React.ReactNode
  trailing?: React.ReactNode
  /** Extra meta lines between the title and the date range. */
  children?: React.ReactNode
}) {
  const displayValue = convertAdjustmentValue(value, type)
  const metaParts = valueInDetail ? [detail, displayValue] : [detail]
  const metaPrefix = metaParts.filter((part) => part !== undefined && part !== "").join(" ")

  return (
    <div className="px-4 py-2.5">
      <div className="flex items-center justify-between gap-2">
        {leading}
        <span className="label-base text-fg-base truncate flex-1 min-w-0">{name}</span>
        {trailing}
        {!valueInDetail && (
          <span className="paragraph-sm font-semibold text-fg-base shrink-0">{displayValue}</span>
        )}
      </div>
      {children}
      <div className="text-fg-subtle paragraph-sm mt-0.5 text-nowrap">
        {metaPrefix && `${metaPrefix} · `}
        {formatDate(startDate)} – {formatDate(endDate)}
      </div>
    </div>
  )
}
